import { isEmpty, isNil, xor } from 'lodash';

import { type CalendarDate, isAfter, isSameMonthAndYear, isWithinInterval, toCalendarDateNullable } from '../dateutils';
import { roundAtSecondDecimal } from '../numbers';
import { LeaseVariousOperationDTO, PostingDTO, VariousOperationDTO } from '../types/dto-app';
import {
  CustomLabel,
  LeasePaymentFrequency,
  LeaseVariousOperation,
  LeaseVariousOperationType,
  Posting,
  PostingType,
  VariousOperation,
  VariousOperationType,
} from '../types/model-app';

import { ACCOUNT_TURNOVER_RENT_CLASS } from './accountClasses';

export const getVariousOperationLabel = (
  variousOperation: VariousOperation | VariousOperationDTO,
  userLanguage: string
) => {
  let label = (variousOperation.labels as CustomLabel[])?.find(
    (variousOperationLabel) => variousOperationLabel.language.toLowerCase() === userLanguage.toLowerCase()
  )?.label;
  // If not in language, we are taking the first one
  label =
    label || (variousOperation.labels && !isEmpty(variousOperation.labels) ? variousOperation.labels[0].label : '');
  return label;
};

export const isVariousOperationApplicableAtDate = (
  leaseVariousOperation: LeaseVariousOperationDTO | LeaseVariousOperation,
  date: CalendarDate
) => {
  const start = toCalendarDateNullable(leaseVariousOperation.startDate);
  if (isNil(start)) {
    return false;
  }

  if (leaseVariousOperation.type === VariousOperationType.ONE_TIME) {
    return isSameMonthAndYear(date, start);
  }

  const end = toCalendarDateNullable(leaseVariousOperation.endDate);
  if (isNil(end) || isAfter(start, end)) {
    return false;
  }

  return isWithinInterval(date, { start, end });
};

export const isPostingARentDiscount = (posting: Posting | PostingDTO) =>
  posting.leaseVariousOperation?.operationType === LeaseVariousOperationType.DISCOUNT &&
  posting.type === PostingType.DEBIT &&
  posting.class === ACCOUNT_TURNOVER_RENT_CLASS;

export const isPostingARentPayment = (posting: Posting | PostingDTO) =>
  posting.invoice && posting.type === PostingType.CREDIT && posting.class === ACCOUNT_TURNOVER_RENT_CLASS;

export const isLeaseVariousOperationADiscount = (leaseVariousOperation: LeaseVariousOperation) =>
  leaseVariousOperation.operationType === LeaseVariousOperationType.DISCOUNT;

export const isLeaseVariousOperationAnOperation = (leaseVariousOperation: LeaseVariousOperation) =>
  leaseVariousOperation.operationType === LeaseVariousOperationType.CHARGE ||
  leaseVariousOperation.operationType === LeaseVariousOperationType.SERVICE;

export const sumLeaseChargeOperationAmounts = (
  leaseVariousOperations: Array<{
    operationType: LeaseVariousOperationType | string | null;
    amount: number | string | null;
  }>
): number =>
  roundAtSecondDecimal(
    leaseVariousOperations.reduce(
      (total, lvo) =>
        (lvo.operationType === LeaseVariousOperationType.FIXED_CHARGE ||
          lvo.operationType === LeaseVariousOperationType.PROVISIONED_CHARGE) &&
        typeof lvo.amount === 'number'
          ? total + lvo.amount
          : total,
      0
    )
  );

export interface LeaseVariousOperationIdentity {
  accountLabelIds?: string[] | null;
  amount: number;
  endDate: string;
  label?: string | null;
  leaseId: string;
  operationType: LeaseVariousOperationType;
  paymentFrequency: LeasePaymentFrequency;
  startDate: string;
  type: VariousOperationType;
  unitId: string;
  vatRateId?: string | null;
}

export const findDuplicateLeaseVariousOperation = <ExistingType extends LeaseVariousOperationIdentity>(
  candidate: LeaseVariousOperationIdentity,
  existingLeaseVariousOperations: ExistingType[]
): ExistingType | undefined =>
  existingLeaseVariousOperations.find(
    (leaseVariousOperation) =>
      leaseVariousOperation.leaseId === candidate.leaseId &&
      leaseVariousOperation.unitId === candidate.unitId &&
      leaseVariousOperation.operationType === candidate.operationType &&
      leaseVariousOperation.type === candidate.type &&
      leaseVariousOperation.paymentFrequency === candidate.paymentFrequency &&
      leaseVariousOperation.startDate === candidate.startDate &&
      leaseVariousOperation.endDate === candidate.endDate &&
      leaseVariousOperation.amount === candidate.amount &&
      (leaseVariousOperation.label ?? '') === (candidate.label ?? '') &&
      (leaseVariousOperation.vatRateId ?? null) === (candidate.vatRateId ?? null) &&
      isEmpty(xor(leaseVariousOperation.accountLabelIds ?? [], candidate.accountLabelIds ?? []))
  );

export const classifyLeaseVariousOperation = (leaseVariousOperations: LeaseVariousOperation[]) => {
  const [operations, discounts] = leaseVariousOperations.reduce(
    (acc: [LeaseVariousOperation[], LeaseVariousOperation[]], leaseVariousOperation) => {
      if (isLeaseVariousOperationAnOperation(leaseVariousOperation)) {
        acc[0].push(leaseVariousOperation);
      } else if (isLeaseVariousOperationADiscount(leaseVariousOperation)) {
        acc[1].push(leaseVariousOperation);
      }
      return acc;
    },
    [[], []]
  );
  return { operations, discounts };
};
